import { useCallback, useContext, useMemo, useRef, useState } from "react";

import { AuthorizationDialog } from "../authorizationDialog/AuthorizationDialog.tsx";
import { DocumentHeaderRefProvider } from "../documentHeader/DocumentHeaderRefProvider.tsx";
import { RequestAuthorization } from "../types/requestAuthorization.ts";
import {
  APIContext,
  type APIResponseEvent,
  type APIResponseSubscriber,
} from "./apiContext.ts";

type Props = {
  children: React.ReactNode;
};

export function APIProvider({ children }: Props) {
  const alreadyDefinedContext = useContext(APIContext);
  if (alreadyDefinedContext !== null) {
    throw new Error("APIProvider must be used only once in the app");
  }

  const [requestAuthorization, setRequestAuthorization] =
    useState<RequestAuthorization | null>(null);

  const subscribers = useRef<APIResponseSubscriber[]>([]);

  const [showMobileKeyPrompt, setShowMobileKeyPrompt] = useState(false);

  const emit = useCallback(
    (obj: APIResponseEvent<unknown, unknown>) => {
      subscribers.current.forEach((cb) => cb(obj));
    },
    [subscribers],
  );

  const subscribe = useCallback(
    (cb: APIResponseSubscriber) => {
      if (typeof cb !== "function") {
        throw new TypeError("Argument 'cb' must be a function");
      }

      subscribers.current.push(cb);

      const unsubscribe = () => {
        const ix = subscribers.current.indexOf(cb);
        subscribers.current.splice(ix, 1);
      };

      return unsubscribe;
    },
    [subscribers],
  );

  const contextValue: APIContext = useMemo(
    () => ({
      emit,
      subscribe,
      setRequestAuthorization,
      showMobileKeyPrompt,
      setShowMobileKeyPrompt,
    }),
    [
      subscribe,
      emit,
      setRequestAuthorization,
      showMobileKeyPrompt,
      setShowMobileKeyPrompt,
    ],
  );

  return (
    <APIContext.Provider value={contextValue}>
      <DocumentHeaderRefProvider>{children}</DocumentHeaderRefProvider>
      {requestAuthorization && (
        <AuthorizationDialog {...requestAuthorization} />
      )}
    </APIContext.Provider>
  );
}
